import React, { useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Nav,
  Navbar,
  Form,
  Button,
  Alert,
  Spinner,
} from "react-bootstrap";
import Logo from "../images/logo.svg";
import Footer from "../footer/index";
import {
  parseUploadedMenu,
  parseLinkedMenu,
  buildParsedMenuJson,
} from "../utils/parseMenuOrchestrator";
import {
  getAiProviderConfig,
  isAiParsingConfigured,
} from "../utils/aiParseMenu";
import "./menuParser.css";

const ACCEPTED =
  ".pdf,.csv,.png,.jpg,.jpeg,.webp,.gif,.html,.htm,application/pdf,text/csv,text/html,image/*";

const MenuParserPage = () => {
  const [mode, setMode] = useState("file"); // file | link
  const [files, setFiles] = useState([]);
  const [menuUrl, setMenuUrl] = useState("");
  const [useAi, setUseAi] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [menuJson, setMenuJson] = useState(null);

  const providerConfig = useMemo(() => getAiProviderConfig(), []);
  const aiReady = useMemo(() => isAiParsingConfigured(), []);

  const resetResult = () => {
    setError("");
    setWarning("");
    setMenuJson(null);
  };

  const handleFileChange = (e) => {
    const selected = e.target.files
      ? Array.from(e.target.files)
      : [];
    setFiles(selected);
    resetResult();
  };

  const validate = () => {
    if (mode === "file") {
      if (!files.length) return "Please choose a menu file.";
      return "";
    }
    if (!menuUrl.trim()) return "Please enter a restaurant menu link.";
    let url = menuUrl.trim();
    if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
    try {
      // eslint-disable-next-line no-new
      new URL(url);
    } catch {
      return "Please enter a valid menu URL.";
    }
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      setMenuJson(null);
      return;
    }

    setLoading(true);
    setError("");
    setWarning("");
    setMenuJson(null);

    try {
      let result;
      let sourceName = "";

      if (mode === "file") {
        sourceName =
          files.length === 1
            ? files[0].name
            : `${files.length} images`;
        result = await parseUploadedMenu({
          file: files[0],
          files,
          useAi,
        });
      } else {
        sourceName = menuUrl.trim();
        result = await parseLinkedMenu({
          url: menuUrl,
          useAi,
        });
      }

      if (!result.items?.length) {
        setError(
          "No menu items were found. Try another file/link, or set the AI API key in .env."
        );
        return;
      }

      if (result.warning) setWarning(result.warning);

      setMenuJson(
        buildParsedMenuJson({
          items: result.items,
          source: mode === "file" ? "file" : "url",
          sourceName,
          parser: result.parser,
          pipelineMeta: result.pipelineMeta,
          provider:
            result.parser === "ai" ? providerConfig.provider : undefined,
        })
      );
    } catch (err) {
      setError(err?.message || "Failed to parse the menu. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const downloadJson = () => {
    if (!menuJson) return;
    const blob = new Blob([JSON.stringify(menuJson, null, 2)], {
      type: "application/json",
    });
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = "menu-items.json";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(href);
  };

  return (
    <div className="menu-parser-wrapper">
      <div className="header headerSolid">
        <Navbar collapseOnSelect expand="lg">
          <Container fluid className="justify-content-between">
            <Navbar.Brand href="/" className="logo">
              <img src={Logo} alt="RestoGPT AI" />
            </Navbar.Brand>
            <Navbar.Toggle aria-controls="menu-parser-nav" />
            <Navbar.Collapse
              id="menu-parser-nav"
              className="justify-content-center center"
            >
              <Nav className="menu">
                <Nav.Link href="https://restogpt.ai/#WORKS">HOW IT WORKS</Nav.Link>
                <Nav.Link href="https://restogpt.ai/#FEATURES">FEATURES</Nav.Link>
                <Nav.Link href="https://restogpt.ai/#BENEFITS">BENEFITS</Nav.Link>
                <Nav.Link href="/menu-parser" className="gen-btn">
                  PARSE MENU
                </Nav.Link>
              </Nav>
            </Navbar.Collapse>
          </Container>
        </Navbar>
      </div>

      <section className="menu-parser-main">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} md={10}>
              <div className="menu-parser-intro">
                <h1>AI menu parser</h1>
                <p>
                  Upload a PDF, CSV, HTML, or menu photo (PNG/JPG/WEBP — select
                  multiple images for multi-page boards) — or paste a menu or
                  image URL. Output is{" "}
                  <code>restogpt-menu-import-v1</code> JSON (
                  <code>categories</code> → items with dollar{" "}
                  <code>price</code>). AI vision reads photos; links are expanded
                  server-side
                  (pagination, lazy-load chunks, item modals, detail pages, SPA
                  bundles). Use <code>npm run dev</code>; large menus can take 1–3
                  minutes. Optional: <code>MENU_PARSER_PLAYWRIGHT=1</code> after{" "}
                  <code>npm install playwright</code>.
                </p>
              </div>

              <Form className="menu-parser-form" onSubmit={handleSubmit}>
                <div
                  className="mode-toggle"
                  role="tablist"
                  aria-label="Input type"
                >
                  <button
                    type="button"
                    className={mode === "file" ? "active" : ""}
                    onClick={() => {
                      setMode("file");
                      resetResult();
                    }}
                  >
                    Upload file
                  </button>
                  <button
                    type="button"
                    className={mode === "link" ? "active" : ""}
                    onClick={() => {
                      setMode("link");
                      resetResult();
                    }}
                  >
                    Menu or image URL
                  </button>
                </div>

                {mode === "file" ? (
                  <Form.Group className="mb-3" controlId="menuFile">
                    <Form.Label>
                      Menu file (PDF, images, CSV, or HTML)
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept={ACCEPTED}
                      multiple
                      onChange={handleFileChange}
                    />
                    <Form.Text muted>
                      Photos need AI enabled and an API key in{" "}
                      <code>.env</code> (OpenAI or DeepSeek). You can select
                      several images at once.
                    </Form.Text>
                  </Form.Group>
                ) : (
                  <Form.Group className="mb-3" controlId="menuUrl">
                    <Form.Label>Restaurant menu URL</Form.Label>
                    <Form.Control
                      type="url"
                      placeholder="https://www.example.com/menu or https://…/menu.jpg"
                      value={menuUrl}
                      onChange={(e) => {
                        setMenuUrl(e.target.value);
                        resetResult();
                      }}
                    />
                  </Form.Group>
                )}

                <Form.Group className="mb-3" controlId="useAi">
                  <Form.Check
                    type="switch"
                    label="Use AI parsing (required for images)"
                    checked={useAi}
                    onChange={(e) => {
                      setUseAi(e.target.checked);
                      resetResult();
                    }}
                  />
                  {useAi && !aiReady && (
                    <Form.Text className="text-danger d-block">
                      No AI key found. Add {providerConfig.envKeyName} to{" "}
                      <code>.env</code> and restart <code>npm start</code>.
                    </Form.Text>
                  )}
                </Form.Group>

                {error && (
                  <Alert variant="danger" className="mb-3">
                    {error}
                  </Alert>
                )}
                {warning && (
                  <Alert variant="warning" className="mb-3">
                    {warning}
                  </Alert>
                )}

                <Button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {useAi ? "AI parsing…" : "Parsing…"}
                    </>
                  ) : (
                    "Submit & parse"
                  )}
                </Button>
              </Form>

              {menuJson && (
                <div className="menu-parser-result">
                  <div className="result-header">
                    <h2>
                      Menu import JSON{" "}
                      <span className="count">
                        ({menuJson.productCount ?? menuJson.itemCount} products
                        · {menuJson.categoryCount ?? 1} categories ·{" "}
                        {menuJson.parser})
                      </span>
                    </h2>
                    <Button
                      variant="outline-dark"
                      size="sm"
                      onClick={downloadJson}
                    >
                      Download JSON
                    </Button>
                  </div>
                  <pre className="json-preview">
                    {JSON.stringify(menuJson, null, 2)}
                  </pre>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </section>

      <Footer />
    </div>
  );
};

export default MenuParserPage;
